/**
 * VFXParticlesWebGPU - TSL/WebGPU node-based particle rendering implementation.
 */

import * as THREE from 'three';
import {
  attribute,
  cameraPosition,
  cameraProjectionMatrix,
  cameraViewMatrix,
  clamp,
  cross,
  directionToColor,
  distance,
  exp,
  float,
  int,
  mix,
  mrt,
  normalize,
  positionGeometry,
  select,
  smoothstep,
  texture,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { MeshBasicNodeMaterial, type Node } from 'three/webgpu';

import { applyEasing } from '../easings.js';
import { AppearanceMode, easeFunctionList, RenderModeEnum } from '../types.js';

import { VFXParticlesShared } from './VFXParticlesShared.js';

function rotateXYZ(point: Node<'vec3'>, rotation: Node<'vec3'>): Node<'vec3'> {
  const cx = rotation.x.cos();
  const sx = rotation.x.sin();
  const cy = rotation.y.cos();
  const sy = rotation.y.sin();
  const cz = rotation.z.cos();
  const sz = rotation.z.sin();

  const rotatedX = point;
  const rotatedY = vec3(
    rotatedX.x,
    rotatedX.y.mul(cx).sub(rotatedX.z.mul(sx)),
    rotatedX.y.mul(sx).add(rotatedX.z.mul(cx))
  );
  const rotatedZ = vec3(
    rotatedY.x.mul(cy).add(rotatedY.z.mul(sy)),
    rotatedY.y,
    rotatedY.x.negate().mul(sy).add(rotatedY.z.mul(cy))
  );

  return vec3(
    rotatedZ.x.mul(cz).sub(rotatedZ.y.mul(sz)),
    rotatedZ.x.mul(sz).add(rotatedZ.y.mul(cz)),
    rotatedZ.z
  );
}

export class VFXParticlesWebGPU extends VFXParticlesShared<MeshBasicNodeMaterial> {
  private uniformNodes?: Record<string, { value: unknown }>;

  private hasUniformNodes(): boolean {
    const uniformNodes = this.uniformNodes;
    return Boolean(
      uniformNodes?.uTime &&
      uniformNodes?.uIntensity &&
      uniformNodes?.uStretchScale &&
      uniformNodes?.uFadeSize &&
      uniformNodes?.uFadeAlpha &&
      uniformNodes?.uGravity &&
      uniformNodes?.uAppearanceMode &&
      uniformNodes?.uEasingFunction
    );
  }

  protected override createMaterial(): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();

    const uTime = uniform(0);
    const uIntensity = uniform(this.settings.intensity);
    const uStretchScale = uniform(this.settings.stretchScale);
    const uFadeSize = uniform(new THREE.Vector2(...this.settings.fadeSize));
    const uFadeAlpha = uniform(new THREE.Vector2(...this.settings.fadeAlpha));
    const uGravity = uniform(new THREE.Vector3(...this.settings.gravity));
    const uAppearanceMode = uniform(this.settings.appearance);
    const uEasingFunction = uniform(easeFunctionList.indexOf(this.settings.easeFunction));

    /** 0 = none, 1 = linear (THREE.Fog), 2 = exp2 (THREE.FogExp2); matches WebGL fog chunks. */
    const uFogMode = uniform(0);
    const uFogNear = uniform(0.1);
    const uFogFar = uniform(50);
    const uFogDensity = uniform(0.04);
    const uFogColor = uniform(new THREE.Vector3(0.5, 0.5, 0.5));

    this.uniformNodes = {
      uTime,
      uIntensity,
      uStretchScale,
      uFadeSize,
      uFadeAlpha,
      uGravity,
      uAppearanceMode,
      uEasingFunction,
      uFogMode,
      uFogNear,
      uFogFar,
      uFogDensity,
      uFogColor,
    };

    const instancePosition = vec3(attribute<'vec3'>('instancePosition', 'vec3'));
    const instanceScale = vec3(attribute<'vec3'>('instanceScale', 'vec3'));
    const instanceRotation = vec3(attribute<'vec3'>('instanceRotation', 'vec3'));
    const instanceSpeed = float(attribute<'float'>('instanceSpeed', 'float'));
    const instanceRotationSpeed = vec3(attribute<'vec3'>('instanceRotationSpeed', 'vec3'));
    const instanceDirection = vec3(attribute<'vec3'>('instanceDirection', 'vec3'));
    const instanceColor = vec3(attribute<'vec3'>('instanceColor', 'vec3'));
    const instanceColorEnd = vec3(attribute<'vec3'>('instanceColorEnd', 'vec3'));
    const instanceLifetime = vec2(attribute<'vec2'>('instanceLifetime', 'vec2'));

    const startTime = instanceLifetime.x;
    const duration = instanceLifetime.y;
    const age = select(
      instanceSpeed.lessThan(0.0),
      duration.sub(uTime.sub(startTime)),
      uTime.sub(startTime)
    );

    const rawProgress = age.div(duration);
    const clampedProgress = clamp(rawProgress, 0.0, 1.0);
    const invalid = rawProgress.lessThan(0.0).or(rawProgress.greaterThanEqual(1.0));
    const easedProgress = applyEasing(clampedProgress, uEasingFunction.toInt());
    const vProgress = varying(select(invalid, rawProgress, easedProgress), 'vProgress');

    const scaleFade = smoothstep(0.0, uFadeSize.x, vProgress).mul(
      smoothstep(1.01, uFadeSize.y, vProgress)
    );

    const normalizedDirection = select(
      instanceDirection.length().greaterThan(0.0),
      normalize(instanceDirection),
      vec3(0.0, 0.0, 0.0)
    );

    const gravityForce = vec3(uGravity).mul(0.5).mul(age.mul(age));
    const easedAge = vProgress.mul(duration);
    const offset = normalizedDirection.mul(easedAge).mul(instanceSpeed).add(gravityForce);
    const worldBase = instancePosition.add(offset);
    const dynRotation = instanceRotation.add(instanceRotationSpeed.mul(age));

    const worldPos = (() => {
      if (this.settings.renderMode === RenderModeEnum.Billboard) {
        const localZ = normalize(cameraPosition.sub(worldBase));
        const worldUp = vec3(0.0, 1.0, 0.0);
        const localX = normalize(cross(worldUp, localZ));
        const localY = cross(localZ, localX);

        const spin = dynRotation.z;
        const c = spin.cos();
        const s = spin.sin();
        const scaled = vec2(
          positionGeometry.x.mul(instanceScale.x).mul(scaleFade),
          positionGeometry.y.mul(instanceScale.y).mul(scaleFade)
        );
        const spun = vec2(
          c.mul(scaled.x).sub(s.mul(scaled.y)),
          s.mul(scaled.x).add(c.mul(scaled.y))
        );

        return worldBase.add(localX.mul(spun.x)).add(localY.mul(spun.y));
      }

      if (this.settings.renderMode === RenderModeEnum.StretchBillboard) {
        const worldVelocity = normalizedDirection.mul(instanceSpeed).add(vec3(uGravity).mul(age));
        const currentSpeed = worldVelocity.length();
        const eyeVector = normalize(cameraPosition.sub(worldBase));
        const tangent = normalize(worldVelocity);
        const projectedTangent = tangent.sub(tangent.dot(eyeVector).mul(eyeVector));

        const particlePlaneUp = select(
          projectedTangent.length().lessThan(0.001),
          tangent,
          normalize(projectedTangent)
        );
        const particlePlaneRight = normalize(cross(particlePlaneUp, eyeVector));

        const width = instanceScale.x.mul(scaleFade);
        const length = instanceScale.y.mul(scaleFade).mul(
          float(1.0).add(currentSpeed.mul(uStretchScale))
        );

        const spin = dynRotation.z;
        const c = spin.cos();
        const s = spin.sin();
        const localSpun = vec2(
          c.mul(positionGeometry.x).sub(s.mul(positionGeometry.y)),
          s.mul(positionGeometry.x).add(c.mul(positionGeometry.y))
        );

        return worldBase
          .add(particlePlaneRight.mul(localSpun.x).mul(width))
          .add(particlePlaneUp.mul(localSpun.y).mul(length));
      }

      const localScaled = vec3(
        positionGeometry.x.mul(instanceScale.x).mul(scaleFade),
        positionGeometry.y.mul(instanceScale.y).mul(scaleFade),
        positionGeometry.z.mul(instanceScale.z).mul(scaleFade)
      );
      const rotated = rotateXYZ(localScaled, dynRotation);
      return worldBase.add(rotated);
    })();

    const mvPosition = cameraViewMatrix.mul(vec4(worldPos, 1.0));
    const vFogDepth = varying(mvPosition.z.negate(), 'vFogDepth');

    material.vertexNode = select(
      invalid,
      vec4(9999.0, 9999.0, 9999.0, 1.0),
      cameraProjectionMatrix.mul(mvPosition)
    );

    const vUv = varying(uv(), 'vUv');
    const vColor = varying(instanceColor, 'vColor');
    const vColorEnd = varying(instanceColorEnd, 'vColorEnd');

    const baseColor = mix(vColor, vColorEnd, vProgress).mul(uIntensity);
    const alphaBase = smoothstep(0.0, uFadeAlpha.x, vProgress).mul(
      smoothstep(1.01, uFadeAlpha.y, vProgress)
    );
    const aliveMask = select(
      vProgress.lessThan(0.0).or(vProgress.greaterThan(1.0)),
      float(0.0),
      float(1.0)
    );

    let finalColor = baseColor;
    let alpha = alphaBase.mul(aliveMask);
    if (this.alphaMap) {
      const texSample = texture(this.alphaMap, vUv);
      finalColor = finalColor.mul(texSample.rgb);
      alpha = texSample.a.mul(alpha);
    } else {
      const circleMask = select(
        uAppearanceMode.toInt().equal(int(AppearanceMode.Circular)),
        select(distance(vUv, vec2(0.5, 0.5)).greaterThan(0.5), float(0.0), float(1.0)),
        float(1.0)
      );
      alpha = alpha.mul(circleMask);
    }

    const fogDepth = vFogDepth;
    const linearFog = smoothstep(uFogNear, uFogFar, fogDepth);
    const exp2Fog = float(1.0).sub(
      exp(uFogDensity.mul(uFogDensity).mul(fogDepth).mul(fogDepth).negate())
    );
    const fogFactor = select(uFogMode.equal(int(2)), exp2Fog, linearFog);
    const fogFactorApplied = select(uFogMode.equal(int(0)), float(0.0), fogFactor);
    const foggedRgb = mix(finalColor, vec3(uFogColor.x, uFogColor.y, uFogColor.z), fogFactorApplied);

    // Use outputNode (not fragmentNode) so NodeMaterial can merge renderer MRT outputs
    // when post-processing enables additional targets (normal, metalrough, emissive, etc.).
    material.outputNode = vec4(foggedRgb, alpha);
    // Match WebGL ShaderMaterial path: fog is applied in our outputNode; disable NodeMaterial fog pass.
    material.fog = false;

    // Override the normal MRT output for this material to mark particles as transparent (alpha=0).
    // Geometry writes alpha=1 to the normal MRT; particles write alpha=0. AOEffect reads this
    // alpha to build a masked depth node that returns sky-depth (1.0) at transparent pixels,
    // so GTAO completely skips particle pixels (depth>=1.0 → discard) and does not use their
    // depth when sampling neighbours for wall-pixel AO → eliminates dark rectangle artifacts.
    // When AO is disabled the normal texture slot is absent; MRTNode.setup() returns index -1
    // which is silently skipped by OutputStructNode.generate() — no crash, no regression.
    material.mrtNode = mrt({ normal: vec4(directionToColor(vec3(0, 0, 1)), 0) });

    return material;
  }

  protected override updateMaterialUniforms(elapsedTime: number): void {
    if (!this.hasUniformNodes()) {
      this.recreateMaterial();
    }

    if (!this.hasUniformNodes()) {
      return;
    }

    const uniformNodes = this.uniformNodes;
    if (!uniformNodes) {
      return;
    }

    uniformNodes.uTime.value = elapsedTime;
    uniformNodes.uIntensity.value = this.settings.intensity;
    uniformNodes.uStretchScale.value = this.settings.stretchScale;
    uniformNodes.uFadeSize.value = new THREE.Vector2(...this.settings.fadeSize);
    uniformNodes.uFadeAlpha.value = new THREE.Vector2(...this.settings.fadeAlpha);
    uniformNodes.uGravity.value = new THREE.Vector3(...this.settings.gravity);
    uniformNodes.uAppearanceMode.value = this.settings.appearance;
    uniformNodes.uEasingFunction.value = easeFunctionList.indexOf(this.settings.easeFunction);

    const fogUniforms = uniformNodes as typeof uniformNodes & {
      uFogMode: { value: number };
      uFogNear: { value: number };
      uFogFar: { value: number };
      uFogDensity: { value: number };
      uFogColor: { value: THREE.Vector3 };
    };
    const sceneFog = this.getMeshScene()?.fog ?? null;
    if (!sceneFog) {
      fogUniforms.uFogMode.value = 0;
    } else if (sceneFog instanceof THREE.FogExp2) {
      fogUniforms.uFogMode.value = 2;
      fogUniforms.uFogDensity.value = sceneFog.density;
      fogUniforms.uFogColor.value.set(sceneFog.color.r, sceneFog.color.g, sceneFog.color.b);
    } else if (sceneFog instanceof THREE.Fog) {
      fogUniforms.uFogMode.value = 1;
      fogUniforms.uFogNear.value = sceneFog.near;
      fogUniforms.uFogFar.value = sceneFog.far;
      fogUniforms.uFogColor.value.set(sceneFog.color.r, sceneFog.color.g, sceneFog.color.b);
    } else {
      fogUniforms.uFogMode.value = 0;
    }
  }
}
